import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle, Loader2, Undo2 } from "lucide-react";
import api from "@/lib/api";

/**
 * The CAN-SPAM opt-out landing page. Public — no session, by law.
 *
 * 15 U.S.C. 7704(a)(3) lets a recipient opt out "without providing any
 * information beyond an email address and an opt-out preference" and without
 * "any step other than sending a reply email or visiting a single Internet Web
 * page". A login is exactly that forbidden step, and it is also the step that
 * fails for someone who signed up with Google on a laptop they no longer have.
 * So this page authenticates nothing: the signed token in the URL is the whole
 * of the authority, and it can only ever switch off the one category it was
 * minted for.
 *
 * **It acts on load, and does not ask first.** Jorge's requirement is an
 * immediate opt-out, and a confirmation button between the click and the effect
 * is the pattern that leaves people believing they unsubscribed when they
 * closed the tab instead. The safety net is the undo below, not a prompt above.
 *
 * **The API call is a POST even though arriving here was a GET**, and that is
 * the whole reason the backend splits them: corporate mail scanners and link
 * previewers fetch every URL in a message, so a GET that unsubscribed would
 * quietly opt people out of mail they wanted, with no way to ever find out why
 * it stopped.
 */
export default function Unsubscribe() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  // idle | working | done | resubscribed | error
  const [state, setState] = useState("working");
  const [category, setCategory] = useState(null);

  const call = useCallback(async (path, next) => {
    setState("working");
    try {
      const { data } = await api.post(`/notifications/${path}?token=${encodeURIComponent(token)}`);
      setCategory(data.category || null);
      setState(next);
    } catch {
      setState("error");
    }
  }, [token]);

  useEffect(() => {
    if (!token) { setState("error"); return; }
    call("unsubscribe", "done");
  }, [token, call]);

  const label = category
    ? t(`settings.notifications.event.${category}.label`, { defaultValue: category })
    : "";

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12">
      <div className="card-base p-8 max-w-md w-full text-center" data-testid="unsubscribe-page">
        {state === "working" && (
          <>
            <Loader2 className="animate-spin mx-auto text-smoke" size={28} />
            <p className="mt-4 text-sm text-smoke">{t("common.loading")}</p>
          </>
        )}

        {state === "done" && (
          <>
            <CheckCircle2 className="mx-auto text-green-600" size={36} />
            <h1 className="mt-4 font-display text-2xl font-bold text-worker">
              {t("unsubscribe.doneTitle")}
            </h1>
            <p className="mt-2 text-sm text-smoke" data-testid="unsubscribe-category">
              {t("unsubscribe.doneBody", { category: label })}
            </p>
            <p className="mt-3 text-xs text-smoke">{t("unsubscribe.stillGet")}</p>
            <button
              onClick={() => call("resubscribe", "resubscribed")}
              className="fx-btn fx-btn--secondary mt-5 mx-auto"
              data-testid="unsubscribe-undo"
            >
              <Undo2 size={16} /> {t("unsubscribe.undo")}
            </button>
          </>
        )}

        {state === "resubscribed" && (
          <>
            <CheckCircle2 className="mx-auto text-hive-deep" size={36} />
            <h1 className="mt-4 font-display text-2xl font-bold text-worker">
              {t("unsubscribe.undoneTitle")}
            </h1>
            <p className="mt-2 text-sm text-smoke">
              {t("unsubscribe.undoneBody", { category: label })}
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <AlertCircle className="mx-auto text-red-600" size={36} />
            <h1 className="mt-4 font-display text-2xl font-bold text-worker">
              {t("unsubscribe.errorTitle")}
            </h1>
            <p className="mt-2 text-sm text-smoke">{t("unsubscribe.errorBody")}</p>
          </>
        )}

        <p className="mt-6 pt-5 border-t border-sand text-xs text-smoke">
          <Link to="/settings?tab=notifications" className="underline">
            {t("unsubscribe.managePrefs")}
          </Link>
        </p>
      </div>
    </div>
  );
}
